/**
 * One in-memory copy of app settings, and one save at a time.
 *
 * Callers pass only the fields they mean to change. Those fields are merged
 * onto the latest copy immediately. The network write always sends that latest
 * copy, and the next write waits until the previous one finishes, then sends
 * again if something changed while it was in flight. An older request cannot
 * replace the blob with a snapshot that is missing a newer edit.
 */

import { applyOverlayAppSettings, patchOverlayAppSetting, OVERLAY_APP_SETTING_KEYS } from './overlayAppSettings';

let latest = null;
let seeded = false;
let localEdits = false;
let listener = null;

let dirty = false;
let flushing = false;
let tail = Promise.resolve();
let saveFnRef = null;

export function getAppSettings() {
  return latest ? { ...latest } : {};
}

export function hasLocalAppSettingsEdits() {
  return localEdits;
}

export function isAppSettingsStoreActive() {
  return seeded || localEdits || latest != null;
}

export function isAppSettingsSeeded() {
  return seeded;
}

/**
 * Load the server blob once. Edits made before it arrives stay on top and are
 * not sent until the server blob is in memory, so a partial save cannot replace it.
 */
/** Replace the store from the server only when the user has not edited yet. */
export function adoptServerAppSettings(appSettings) {
  if (localEdits) return getAppSettings();
  latest = applyOverlayAppSettings({ ...(appSettings || {}) });
  seeded = true;
  return getAppSettings();
}

export function seedAppSettingsFromServer(appSettings) {
  if (appSettings == null || typeof appSettings !== 'object') return getAppSettings();
  if (seeded) {
    if (localEdits) return getAppSettings();
    if (latest && Object.keys(latest).length > 0) return getAppSettings();
  }
  const pending = !seeded && localEdits ? latest : null;
  latest = applyOverlayAppSettings({ ...appSettings, ...(pending || {}) });
  seeded = true;
  return getAppSettings();
}

export function resetAppSettingsStore() {
  latest = null;
  seeded = false;
  localEdits = false;
  dirty = false;
  saveFnRef = null;
}

export function subscribeAppSettings(fn) {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

function applyPatch(base, patch) {
  const next = { ...(base || {}) };
  for (const key of Object.keys(patch)) {
    if (patch[key] === undefined) delete next[key];
    else next[key] = patch[key];
  }
  return next;
}

/**
 * @param {object|function} patchOrFn Top-level fields to write. `undefined` deletes a field.
 * A function receives the latest copy and returns those fields.
 */
export function commitAppSettingsPatch(patchOrFn) {
  const base = getAppSettings();
  const patch = typeof patchOrFn === 'function' ? patchOrFn(base) : patchOrFn;
  if (!patch || typeof patch !== 'object' || Object.keys(patch).length === 0) {
    return base;
  }
  const next = applyPatch(latest || {}, patch);
  const changed = JSON.stringify(next) !== JSON.stringify(latest || {});
  latest = next;
  seeded = true;
  if (!changed) return getAppSettings();
  localEdits = true;
  for (const key of OVERLAY_APP_SETTING_KEYS) {
    if (Object.prototype.hasOwnProperty.call(patch, key) && typeof patch[key] === 'boolean') {
      patchOverlayAppSetting(key, patch[key]);
    }
  }
  listener?.(getAppSettings());
  return getAppSettings();
}

/**
 * Send the latest snapshot. If another patch lands while the request is open,
 * send once more after it finishes. Only one request is in flight.
 */
export function enqueueAppSettingsSave(saveFn) {
  saveFnRef = saveFn;
  dirty = true;
  if (flushing) return tail;
  flushing = true;
  const run = saveFn;
  tail = (async () => {
    try {
      while (dirty) {
        dirty = false;
        const body = getAppSettings();
        await run(body);
      }
      return getAppSettings();
    } finally {
      flushing = false;
      if (dirty && saveFnRef) {
        enqueueAppSettingsSave(saveFnRef);
      }
    }
  })();
  return tail;
}
